/**
 * Chat API (message + streaming)
 */

import type { ChatResponse, ChatCard } from '../types';
import { fetchApi, getApiBase, getAuthHeaders } from './fetch';

export interface ChatPageContext {
  currentView?: string;
  currentViewName?: string;
  selectedTaskId?: string;
  selectedTaskContent?: string;
  selectedTaskDescription?: string;
  selectedTaskPriority?: number;
  selectedTaskDueDate?: string;
  selectedTaskCategory?: string;
  selectedTaskLabels?: string[];
}

export async function sendChatMessage(
  message: string,
  history?: Array<{ role: 'user' | 'assistant'; content: string }>,
  context?: ChatPageContext
): Promise<ChatResponse> {
  return fetchApi<ChatResponse>('/chat', {
    method: 'POST',
    body: JSON.stringify({ message, history, context }),
  });
}

export interface StreamCallbacks {
  onStatus?: (message: string) => void;
  onTool?: (name: string, description: string) => void;
  onToken?: (text: string) => void;
  onActions?: (actions: Array<{ type: string; description: string; taskId?: string; viewSlug?: string }>) => void;
  onCards?: (cards: ChatCard[]) => void;
  onDone?: () => void;
  onError?: (message: string) => void;
}

export async function sendChatMessageStream(
  message: string,
  callbacks: StreamCallbacks,
  history?: Array<{ role: 'user' | 'assistant'; content: string }>,
  context?: ChatPageContext
): Promise<void> {
  const apiBase = getApiBase();

  try {
    const response = await fetch(`${apiBase}/chat/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ message, history, context }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error?.message || `HTTP ${response.status}`;
      callbacks.onError?.(errorMessage);
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      callbacks.onError?.('No response body');
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = '';

      let currentEvent = '';
      let currentData = '';

      for (const line of lines) {
        if (line.startsWith('event:')) {
          currentEvent = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          currentData = line.slice(5).trim();
        } else if (line === '' && currentEvent && currentData) {
          try {
            const data = JSON.parse(currentData);
            processSSEEvent(currentEvent, data, callbacks);
          } catch {
            // Ignore malformed JSON
          }
          currentEvent = '';
          currentData = '';
        } else if (line !== '') {
          buffer += line + '\n';
        }
      }
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Stream error';
    callbacks.onError?.(message);
  }
}

function processSSEEvent(event: string, data: any, callbacks: StreamCallbacks): void {
  switch (event) {
    case 'status':
      callbacks.onStatus?.(data.message);
      break;
    case 'tool':
      callbacks.onTool?.(data.name, data.description);
      break;
    case 'token':
      callbacks.onToken?.(data.text);
      break;
    case 'actions':
      callbacks.onActions?.(data);
      break;
    case 'cards':
      callbacks.onCards?.(data);
      break;
    case 'done':
      callbacks.onDone?.();
      break;
    case 'error':
      callbacks.onError?.(data.message);
      break;
  }
}
